import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { KeyRound, LogOut, Monitor, Globe, ShieldCheck, Smartphone, Trash2 } from 'lucide-react';
import type { DeviceKind, DeviceSession } from '@mailpilot/shared';
import { api } from '../../lib/api';
import { qk } from '../../lib/keys';
import { fullDate, relativeTime } from '../../lib/format';
import { Badge, Button, Card, CardBody, CardDivider, CardHeader, EmptyState, ErrorState, Field, InlineError, Input, Skeleton, useConfirm, useToast } from '../../components/ui';
import { FormFooter, SettingRow, StatusPill } from '../../components/settings/SettingsPrimitives';
import { useClearLocalSession } from '../../components/settings/SecuritySignOut';
import { SettingsTitle } from './SettingsLayout';
import { TwoFactorCard } from '../../components/settings/TwoFactorCard';

const kindMeta: Record<DeviceKind, { label: string; Icon: typeof Monitor }> = {
  desktop: { label: 'Desktop app', Icon: Monitor },
  web: { label: 'Web browser', Icon: Globe },
  'mobile-web': { label: 'Mobile browser', Icon: Smartphone },
};

function SessionRow({ s, onRevoke, onSignOut, busy }: { s: DeviceSession; onRevoke: () => void; onSignOut: () => void; busy: boolean }) {
  const { label, Icon } = kindMeta[s.deviceKind] ?? kindMeta.web;
  return (
    <li className="group flex flex-wrap items-center gap-x-4 gap-y-3 px-6 py-4 transition-colors hover:bg-raised/40">
      <span className={clsx('flex h-10 w-10 shrink-0 items-center justify-center rounded-[13px]', s.current ? 'bg-primary-soft text-primary-ink' : 'bg-sunken text-muted')} title={label}>
        <Icon className="h-5 w-5" aria-hidden />
        <span className="sr-only">{label}</span>
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate font-medium text-ink">{s.deviceName}</span>
          {s.current && <StatusPill tone="success">This device</StatusPill>}
          <Badge>{label}</Badge>
        </div>
        <p className="mt-0.5 text-xs text-muted">
          Signed in {fullDate(s.createdAt)} · Last seen <span title={fullDate(s.lastSeenAt)}>{relativeTime(s.lastSeenAt)}</span>
        </p>
        {s.userAgent && <p className="mt-0.5 truncate text-[0.6875rem] text-subtle" title={s.userAgent}>{s.userAgent}</p>}
      </div>
      <div className="shrink-0">
        {s.current ? (
          <Button size="sm" icon={<LogOut className="h-4 w-4" />} onClick={onSignOut}>
            Sign out
          </Button>
        ) : (
          <Button size="sm" variant="secondary" icon={<Trash2 className="h-4 w-4" />} loading={busy} onClick={onRevoke} className="hover:!border-danger/40 hover:!bg-danger-soft hover:!text-danger-ink">
            Revoke
          </Button>
        )}
      </div>
    </li>
  );
}

function PasswordForm() {
  const toast = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [touched, setTouched] = useState(false);
  const change = useMutation({
    mutationFn: () => api('POST /api/auth/password', { body: { currentPassword: current, newPassword: next } }),
    onSuccess: () => {
      toast.success('Password changed');
      setCurrent('');
      setNext('');
      setConfirm('');
      setTouched(false);
    },
  });
  const tooShort = next.length > 0 && next.length < 8;
  const mismatch = confirm.length > 0 && next !== confirm;
  const valid = current.length > 0 && next.length >= 8 && next === confirm;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (valid) change.mutate();
  };
  return (
    <form onSubmit={submit} className="space-y-5" noValidate>
      <Field label="Current password" required className="max-w-sm">
        {(id) => <Input id={id} type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />}
      </Field>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="New password" required hint="At least 8 characters." error={tooShort || (touched && next.length < 8) ? 'Use at least 8 characters.' : undefined}>
          {(id) => <Input id={id} type="password" autoComplete="new-password" minLength={8} value={next} onChange={(e) => setNext(e.target.value)} />}
        </Field>
        <Field label="Confirm new password" required error={mismatch || (touched && next !== confirm) ? 'Passwords do not match.' : undefined}>
          {(id) => <Input id={id} type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />}
        </Field>
      </div>
      <InlineError error={change.error} />
      <FormFooter note="You'll stay signed in on this device.">
        <Button type="submit" variant="primary" icon={<KeyRound className="h-4 w-4" />} loading={change.isPending} disabled={!valid && touched}>
          Change password
        </Button>
      </FormFooter>
    </form>
  );
}

export function SecuritySettings() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const clearSession = useClearLocalSession();
  const sessions = useQuery({ queryKey: qk.sessions, queryFn: () => api('GET /api/auth/sessions') });
  const [revoking, setRevoking] = useState<string | null>(null);
  const [signingOut, setSigningOut] = useState(false);

  const revoke = useMutation({
    mutationFn: (id: string) => api('DELETE /api/auth/sessions/:id', { params: { id } }),
    onMutate: (id) => setRevoking(id),
    onSettled: () => setRevoking(null),
    onSuccess: () => {
      toast.success('Device signed out');
      void qc.invalidateQueries({ queryKey: qk.sessions });
    },
    onError: (e) => toast.error(e),
  });

  const askRevoke = async (s: DeviceSession) => {
    const ok = await confirm({
      title: `Revoke ${s.deviceName}?`,
      message: 'That device will be signed out immediately and must sign in again. If it is a desktop app, it will stop ringing on that device.',
      confirmLabel: 'Revoke access',
      danger: true,
    });
    if (ok) revoke.mutate(s.id);
  };

  const signOut = async () => {
    setSigningOut(true);
    try {
      await api('POST /api/auth/logout');
    } catch {
      /* clear locally regardless */
    }
    await clearSession('/login');
  };

  const list = sessions.data?.sessions ?? [];
  const sorted = [...list].sort((a, b) => (a.current === b.current ? new Date(b.lastSeenAt).getTime() - new Date(a.lastSeenAt).getTime() : a.current ? -1 : 1));

  return (
    <div>
      <SettingsTitle title="Security & devices" description="See where you're signed in, revoke devices you don't recognise and keep your password up to date." />
      <div className="space-y-6">
        <Card>
          <CardHeader
            icon={<ShieldCheck className="h-5 w-5" />}
            title="Signed-in devices"
            description="Every desktop app and browser currently signed in to your MailPilot account."
            actions={sorted.length > 0 ? <StatusPill tone="primary">{sorted.length} active</StatusPill> : undefined}
          />
          {sessions.isLoading ? (
            <CardBody className="space-y-4" aria-busy="true">
              {[0, 1, 2].map((i) => (
                <div key={i} className="flex items-center gap-3">
                  <Skeleton className="h-10 w-10 rounded-[13px]" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-3.5 w-1/3" />
                    <Skeleton className="h-3 w-2/3" />
                  </div>
                  <Skeleton className="h-8 w-20 rounded-lg" />
                </div>
              ))}
            </CardBody>
          ) : sessions.error ? (
            <CardBody>
              <ErrorState compact error={sessions.error} onRetry={() => void sessions.refetch()} />
            </CardBody>
          ) : sorted.length === 0 ? (
            <EmptyState icon={<Monitor className="h-6 w-6" />} title="No active sessions" description="Sign in on a device and it will appear here." />
          ) : (
            <>
              <CardDivider className="mt-5" />
              <ul className="divide-y divide-line/70">
                {sorted.map((s) => (
                  <SessionRow key={s.id} s={s} busy={revoking === s.id} onRevoke={() => void askRevoke(s)} onSignOut={() => void signOut()} />
                ))}
              </ul>
            </>
          )}
        </Card>

        <Card>
          <CardHeader icon={<KeyRound className="h-5 w-5" />} title="Change password" description="Use a long passphrase you don't use anywhere else." />
          <CardBody>
            <PasswordForm />
          </CardBody>
        </Card>

        <TwoFactorCard />

        <Card>
          <CardHeader icon={<LogOut className="h-5 w-5" />} title="Sign out" description="End your session on this device." />
          <CardDivider className="mt-5" />
          <CardBody className="!py-5">
            <SettingRow
              label="Sign out of this device"
              description="Signs out this device and removes cached mail stored on it for offline use."
              control={
                <Button variant="primary" size="sm" icon={<LogOut className="h-4 w-4" />} loading={signingOut} onClick={() => void signOut()}>
                  Sign out
                </Button>
              }
            />
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
